// Track B PR3 — 5-stage horizontal lifecycle bar.
//
// Renders the canonical conversation lifecycle per `docs/plan-thread-ui.md`:
//
//   CREATED  →  RECORDING  →  ENDED  →  ANALYZING  →  ANALYZED
//
// The active stage is highlighted in blue, completed stages show a green
// check, and future stages are dimmed. iOS will mirror the same visual
// once desktop is locked in.
//
// State derivation
// ----------------
// Stage is computed from two pieces of state that come from the backend:
//
//   - `recordingState` — `Conversation.recording_state` enum
//     (`"CREATED" | "ACTIVE" | "ENDED" | "ERROR" | null/undefined`).
//
//       NOTE: backend uses `ACTIVE` for the in-progress (live) state. The
//       UI label is "RECORDING" because that's what the user understands.
//       Both `ACTIVE` and the legacy/synonym `RECORDING` are treated as the
//       in-progress stage.
//
//   - `analysisState` — derived hint from the analysis-run row's status.
//     The page using this component is expected to derive it from the
//     latest `ThreadAnalysisRun` for the thread/conversation. The
//     component itself only needs three buckets: `"queued" | "running"`
//     (= ANALYZING), `"committed"` (= ANALYZED), and anything else (no
//     analysis ever started, or it failed).
//
//   - `latestAnalysisRunStatus` (alias for `analysisState`) — accepted as
//     a separate prop so callers that already track the run object can pass
//     `run.status` directly without rederiving a string.
//
//   - `analysisCommitted` — explicit boolean for "the run committed";
//     callers can set this from `run.committed_at != null`. If `true`,
//     the bar shows ANALYZED regardless of `latestAnalysisRunStatus`.
//
// Size variants
// -------------
//   `size="sm"` — thread-view (one bar per conversation in the recordings
//                 list). Compact, ~24px tall.
//   `size="md"` — conversation-detail header. Roomy, ~40px tall.
//
// The component is deliberately presentational — no fetches, no SSE.
// Parent owns state and re-renders on event arrival.

import React from "react";
import { Box, Flex, Text, HStack, Tooltip, Icon } from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";

export const LIFECYCLE_STAGES = [
  "CREATED",
  "RECORDING",
  "ENDED",
  "ANALYZING",
  "ANALYZED",
];

/**
 * Map (recordingState, analysisState, analysisCommitted) → current stage idx.
 *
 * Decision table (first match wins):
 *
 *   analysisCommitted=true                                  → ANALYZED (4)
 *   latestAnalysisRunStatus in {queued, started, running}   → ANALYZING (3)
 *   recordingState === "ENDED"                              → ENDED (2)
 *   recordingState in {"ACTIVE", "RECORDING"}               → RECORDING (1)
 *   recordingState === "CREATED" or undefined               → CREATED (0)
 *   recordingState === "ERROR"                              → ENDED (2) — the
 *       lifecycle bar doesn't have a FAILED slot in v1; the page surfaces the
 *       error separately. Stopping at ENDED communicates "no further progress"
 *       without claiming the analysis ran.
 *
 * Exported for the test suite.
 */
export function computeStageIndex({
  recordingState,
  latestAnalysisRunStatus,
  analysisCommitted,
}) {
  if (analysisCommitted) return 4;
  const a = (latestAnalysisRunStatus || "").toLowerCase();
  if (a === "queued" || a === "running" || a === "started") return 3;
  const r = recordingState || "CREATED";
  if (r === "ENDED" || r === "ERROR") return 2;
  if (r === "ACTIVE" || r === "RECORDING") return 1;
  return 0;
}

const SIZE_TOKENS = {
  sm: {
    dotSize: 4,
    labelFontSize: "2xs",
    connectorH: "1px",
    gap: 1,
    iconBox: 4,
    labelMt: 0,
  },
  md: {
    dotSize: 6,
    labelFontSize: "xs",
    connectorH: "2px",
    gap: 2,
    iconBox: 6,
    labelMt: 1,
  },
};

const ConversationLifecycleBar = ({
  recordingState,
  analysisState,
  latestAnalysisRunStatus,
  analysisCommitted = false,
  size = "md",
}) => {
  // analysisState is the legacy prop name; latestAnalysisRunStatus is what
  // the plan uses. Either works; latest wins if both are passed.
  const runStatus = latestAnalysisRunStatus ?? analysisState ?? null;

  const currentIdx = computeStageIndex({
    recordingState,
    latestAnalysisRunStatus: runStatus,
    analysisCommitted,
  });

  const tokens = SIZE_TOKENS[size] || SIZE_TOKENS.md;

  return (
    <HStack
      spacing={tokens.gap}
      align="center"
      data-testid="conversation-lifecycle-bar"
      data-current-stage={LIFECYCLE_STAGES[currentIdx]}
      data-size={size}
    >
      {LIFECYCLE_STAGES.map((stage, idx) => {
        const isPast = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const isFuture = idx > currentIdx;

        const stateLabel = isCurrent
          ? "current"
          : isPast
          ? "past"
          : "future";

        let bg;
        let color;
        if (isCurrent) {
          bg = "blue.500";
          color = "white";
        } else if (isPast) {
          bg = "green.500";
          color = "white";
        } else {
          bg = "gray.200";
          color = "gray.500";
        }

        return (
          <React.Fragment key={stage}>
            <Tooltip label={stage} placement="bottom" hasArrow>
              <Flex
                direction="column"
                align="center"
                data-testid={`lifecycle-stage-${stage}`}
                data-stage-state={stateLabel}
                opacity={isFuture ? 0.45 : 1}
              >
                <Box
                  bg={bg}
                  color={color}
                  borderRadius="full"
                  w={tokens.iconBox}
                  h={tokens.iconBox}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={tokens.labelFontSize}
                  fontWeight="bold"
                >
                  {isPast ? (
                    <Icon as={CheckIcon} boxSize={size === "sm" ? 2 : 3} />
                  ) : (
                    <Text fontSize={tokens.labelFontSize} as="span">
                      {idx + 1}
                    </Text>
                  )}
                </Box>
                {size === "md" && (
                  <Text
                    fontSize={tokens.labelFontSize}
                    color={
                      isCurrent
                        ? "blue.600"
                        : isPast
                        ? "green.600"
                        : "gray.500"
                    }
                    mt={tokens.labelMt}
                    fontWeight={isCurrent ? "semibold" : "normal"}
                  >
                    {stage}
                  </Text>
                )}
              </Flex>
            </Tooltip>
            {idx < LIFECYCLE_STAGES.length - 1 && (
              <Box
                flex="1"
                minW={size === "sm" ? "8px" : "20px"}
                h={tokens.connectorH}
                bg={idx < currentIdx ? "green.300" : "gray.200"}
                data-testid={`lifecycle-connector-${idx}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </HStack>
  );
};

export default ConversationLifecycleBar;
